<modal-dialog #dialog [dialogTemplate]="dialogTemplate"></modal-dialog>

<ng-template #dialogTemplate>
  <!-- App-wide modal shell: sticky header with dismiss, optional subtitle,
  body, then sticky footer. Esc-to-cancel handled by @HostListener on
  document:keydown.escape in the component. -->
  <div class="flex-left-column light-control-host">
    <div class="modal-header">
      <h3 class="panel-title">{{ title }}</h3>
      <button
        type="button"
        class="modal-close-btn"
        (click)="onCancel()"
        pTooltip="Close"
        tooltipPosition="left"
        aria-label="Close"
      >
        <svg lucideX></svg>
      </button>
    </div>

    <!-- Edit-mode subtitle. Create-mode SAML has a longer instructional block
    in the body below, so no subtitle there. -->
    @if (isEdit && isSaml) {
      <div class="modal-subtitle">
        This will replace the existing identity provider configuration with new configuration.
      </div>
    }

    <form [formGroup]="form" (ngSubmit)="onSave()">
      <!-- SAML Configuration Instructions (Create Only) -->
      @if (isSaml && isCreate) {
        <div>
          <p>
            Use the following configuration to tell your identity provider about Arpio. Then take the XML metadata
            generated by your identity provider, and paste it below. You will then be able to invite users to login to
            {{ uiService.productName }} with their single sign-on credentials.
          </p>
          <div class="margin-top">
            <h3>SAML Configuration</h3>
            <span class="data-label">ACS URL</span>
            <div class="code small">
              {{ apiService.samlAcsUrl() }}
              <a class="copy-clipboard" [copy-clipboard]="apiService.samlAcsUrl()">
                <svg lucideCopy></svg>
              </a>
            </div>
          </div>
          <div class="margin-top">
            <span class="data-label">Audience Restriction / Entity ID</span>
            <div class="code small">
              {{ audienceUri() }}
              <a class="copy-clipboard" [copy-clipboard]="audienceUri()">
                <svg lucideCopy></svg>
              </a>
            </div>
          </div>
        </div>
      }

      <!-- Name Field -->
      <label [for]="'idp-name-' + data.type"><h4 class="margin-top">Name</h4></label>
      @if (isMsEntra) {
        <div class="flex-center-row">
          <input [id]="'idp-name-' + data.type" type="text" formControlName="name" class="arpio-input spacer" />
        </div>
      }
      @if (isSaml) {
        <input
          [id]="'idp-name-' + data.type"
          formControlName="name"
          style="width: 10rem"
          class="arpio-input"
          placeholder="e.g. Okta, or Google"
        />
      }

      <!-- SAML Metadata Field -->
      @if (isSaml) {
        <div>
          <label><h4 class="margin-top">Metadata (XML)</h4></label>
          <textarea
            formControlName="metadata"
            class="arpio-input code small"
            [class.full-width]="isCreate"
            spellcheck="false"
            [placeholder]="isCreate ? '<xml> ... </xml>' : ''"
          ></textarea>
        </div>
      }

      <!-- MS Entra Tenant ID Field -->
      @if (isMsEntra) {
        <div>
          <label [for]="'tenant-id-' + data.operation"><h4 class="margin-top">Directory ID</h4></label>
          <div class="flex-center-row">
            <input
              [id]="'tenant-id-' + data.operation"
              type="text"
              formControlName="tenantId"
              class="arpio-input spacer"
            />
          </div>
          <!-- Use for Authentication Checkbox - MS Entra Only-->
          <div class="flex gap-4 margin-top">
            <div style="margin-top: 3px">
              <input type="checkbox" formControlName="useForAuthentication" class="arpio-checkbox" />
            </div>
            <div>
              <label> Use for Arpio console authentication </label>
              <div class="field-description">
                When enabled, users will be able to login to the Arpio console using accounts from this Microsoft Entra
                directory.
              </div>
            </div>
          </div>
        </div>
      }

      <!-- Auto Provision Checkbox — RBAC-only. -->
      @if (rbacEnabled && useForAuthentication) {
        <div class="flex gap-4 margin-top">
          <div style="margin-top: 3px">
            <input type="checkbox" formControlName="autoCreateSubjects" class="arpio-checkbox" />
          </div>
          <div>
            <label> Automatically provision new users </label>
            <div class="field-description">
              When enabled, new users authenticated through this identity provider will be automatically created in
              Arpio. This requires RBAC to ensure users receive appropriate permissions.
            </div>
          </div>
        </div>
      }

      <!-- Error Display -->
      @if (error) {
        <p class="idp-error">
          {{ error }}
        </p>
      }

      <!-- Footer: primary action right, cancel to its left, matching the
      app-wide .modal-footer pattern. Submit stays inside the form so
      Enter still triggers save. -->
      <div class="modal-footer">
        <button type="button" class="arpio-secondary-button" [disabled]="loading" (click)="onCancel()">Cancel</button>
        <button type="submit" busy-button [busy]="loading" [disabled]="loading || !form.valid" class="arpio-button">
          Save
        </button>
      </div>
    </form>
  </div>
</ng-template>
